import React, { useState, useMemo } from 'react';
import Page from '../components/Page';
import { useDayBook } from '../controllers/financial.controller';
import { useStoreSettings } from '../controllers/settings.controller';
import { 
  IconEye, 
  IconPrinter, 
  IconFileSpreadsheet, 
  IconFileText, 
  IconRefresh, 
  IconChevronLeft, 
  IconChevronRight,
  IconCalendar,
  IconSearch,
  IconX,
  IconCheck,
  IconCoins,
  IconArrowUpRight,
  IconArrowDownRight,
  IconBuildingBank
} from '@tabler/icons-react';
import { toast } from 'react-hot-toast';
import apiClient from '../helpers/ApiClient';

export default function FinancialDayBookPage() {
  // Navigation sub-view: 'daily' | 'range' | 'transactions'
  const [activeTab, setActiveTab] = useState('daily');
  
  // Date states
  const [filterType, setFilterType] = useState('today'); // 'today' | 'yesterday' | 'custom' | 'range'
  const [fromDate, setFromDate] = useState(() => {
    // Default to today in local timezone
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });
  const [toDate, setToDate] = useState(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });

  // Drilldown modal state
  const [drillModal, setDrillModal] = useState({ isOpen: false, title: '', type: '', items: [] });
  const [drillSearch, setDrillSearch] = useState('');

  // Opening balance edit modal
  const [isOpeningModalOpen, setIsOpeningModalOpen] = useState(false);
  const [newOpeningBalance, setNewOpeningBalance] = useState('');
  const [isSavingOB, setIsSavingOB] = useState(false);

  // Queries
  const { data, isLoading, isError, mutate } = useDayBook(fromDate, toDate);
  const { data: storeSettings } = useStoreSettings();

  const store = storeSettings || data?.storeInfo || {};
  const storeName = store?.store_name || store?.name || "Test Restaurant";
  const storeAddress = store?.address || store?.store_address || "Birtamode-2";
  const storePhone = store?.phone || store?.store_phone || "9847474777";
  const storePan = store?.pan_no || store?.tax_number || store?.pan || "61243678";
  const currency = store?.currency || "NPR";

  // Quick period changer
  const handlePeriodPreset = (type) => {
    setFilterType(type);
    const now = new Date();
    if (type === 'today') {
      const d = formatDateStr(now);
      setFromDate(d);
      setToDate(d);
    } else if (type === 'yesterday') {
      const y = new Date(now);
      y.setDate(y.getDate() - 1);
      const d = formatDateStr(y);
      setFromDate(d);
      setToDate(d);
    } else if (type === 'custom') {
      // Keep fromDate, setToDate equal
      setToDate(fromDate);
    }
  };

  const handleShiftPeriod = (days) => {
    const from = new Date(fromDate);
    const to = new Date(toDate);
    from.setDate(from.getDate() + days);
    to.setDate(to.getDate() + days);
    setFromDate(formatDateStr(from));
    setToDate(formatDateStr(to));
  };

  function formatDateStr(d) {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  function formatDisplayDate(dateStr) {
    if (!dateStr) return '';
    try {
      const [y, m, d] = dateStr.split('-');
      const date = new Date(y, m - 1, d);
      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  }

  // Calculate financial fields
  const invSummary = data?.invoiceSummary || {};
  const grossSales = Number(invSummary.total_gross || 0);
  const discount = Number(invSummary.total_discount || 0);
  const tax = Number(invSummary.total_tax || 0);
  const roundOff = Number(invSummary.total_round_off || 0);
  const complementary = Number(invSummary.total_complementary || 0);
  const netSales = Number(invSummary.net_sales || 0);
  const totalBilled = grossSales - discount + tax + roundOff; // or netSales
  const salesDue = Number(data?.total_sales_due || 0);

  // Collections breakdown
  const salesPayments = data?.sales_payments || [];
  let totalCollected = 0;
  salesPayments.forEach(p => {
    totalCollected += Number(p.total_amount || 0);
  });

  // Cash Flow breakdown
  const openingBalance = Number(data?.opening_balance || 0);
  const cashPaymentObj = salesPayments.find(p => (p.payment_method || '').toLowerCase() === 'cash');
  const cashSales = Number(cashPaymentObj?.total_amount || 0);
  const previousDueCollected = Number(data?.due_collected || 0);
  const otherCashReceipts = Number(data?.total_transfers_in || 0) + Number(data?.total_journal_in || 0);
  const totalCashIn = cashSales + previousDueCollected + otherCashReceipts;

  const cashPurchases = Number(cashPaymentObj?.purchases_paid || 0);
  const cashExpenses = Number(cashPaymentObj?.expenses_paid || 0) || Number(data?.total_expenses || 0);
  const cashRefunds = 0;
  const cashWithdrawals = Number(data?.total_transfers_out || 0);
  const otherCashPayments = Number(data?.total_journal_out || 0);
  const totalCashOut = cashPurchases + cashExpenses + cashRefunds + cashWithdrawals + otherCashPayments;

  const expectedClosingCash = openingBalance + totalCashIn - totalCashOut;

  // Traceability drill-down modal opener
  const openDrillDown = (title, type) => {
    const invoices = data?.invoicesList || [];
    const compItems = data?.complementaryList || [];
    let items = [];

    if (type === 'gross' || type === 'net' || type === 'billed') {
      items = invoices;
    } else if (type === 'discount') {
      items = invoices.filter(i => Number(i.discount || 0) > 0);
    } else if (type === 'tax') {
      items = invoices.filter(i => Number(i.tax_total || 0) > 0);
    } else if (type === 'round_off') {
      items = invoices.filter(i => Number(i.round_off || 0) !== 0);
    } else if (type === 'complementary') {
      items = compItems;
    } else if (type === 'due') {
      items = invoices.filter(i => Number(i.due_amount || 0) > 0);
    } else if (type.startsWith('payment_')) {
      const method = type.replace('payment_', '').toLowerCase();
      items = invoices.filter(i => (i.payment_method_name || 'Cash').toLowerCase().includes(method));
    } else {
      items = invoices;
    }

    setDrillModal({ isOpen: true, title, type, items });
    setDrillSearch('');
  };

  // Filtered drill items
  const filteredDrillItems = useMemo(() => {
    if (!drillSearch) return drillModal.items;
    const q = drillSearch.toLowerCase();
    return drillModal.items.filter(item => {
      return (
        (item.invoice_no && item.invoice_no.toLowerCase().includes(q)) ||
        (item.token_no && String(item.token_no).includes(q)) ||
        (item.customer_name && item.customer_name.toLowerCase().includes(q)) ||
        (item.item_name && item.item_name.toLowerCase().includes(q)) ||
        (item.payment_method_name && item.payment_method_name.toLowerCase().includes(q))
      );
    });
  }, [drillModal.items, drillSearch]);

  // Export to CSV
  const handleExportCSV = () => {
    let csv = `DAY BOOK FINANCIAL REPORT\n`;
    csv += `Restaurant: ${storeName}\n`;
    csv += `Location: ${storeAddress}\n`;
    csv += `Phone: ${storePhone}\n`;
    csv += `PAN/VAT: ${storePan}\n`;
    csv += `Period: ${fromDate} to ${toDate}\n\n`;

    csv += `SALES SUMMARY\n`;
    csv += `Item,Amount (${currency})\n`;
    csv += `Gross Sales,${grossSales.toFixed(2)}\n`;
    csv += `Discount,-${discount.toFixed(2)}\n`;
    csv += `Net Sales,${netSales.toFixed(2)}\n`;
    csv += `VAT / Tax,${tax.toFixed(2)}\n`;
    csv += `Round Off,${roundOff.toFixed(2)}\n`;
    csv += `Complementary,${complementary.toFixed(2)}\n`;
    csv += `Total Billed,${totalBilled.toFixed(2)}\n\n`;

    csv += `COLLECTION SUMMARY\n`;
    csv += `Payment Type,Amount (${currency})\n`;
    salesPayments.forEach(p => {
      csv += `${p.payment_method},${Number(p.total_amount || 0).toFixed(2)}\n`;
    });
    csv += `Total Collected,${totalCollected.toFixed(2)}\n`;
    csv += `Sales Due,${salesDue.toFixed(2)}\n\n`;

    csv += `CASH FLOW\n`;
    csv += `Cash Opening,${openingBalance.toFixed(2)}\n`;
    csv += `Cash Sales,${cashSales.toFixed(2)}\n`;
    csv += `Previous Due Collection,${previousDueCollected.toFixed(2)}\n`;
    csv += `Other Cash Receipts,${otherCashReceipts.toFixed(2)}\n`;
    csv += `Cash Purchases,-${cashPurchases.toFixed(2)}\n`;
    csv += `Cash Expenses,-${cashExpenses.toFixed(2)}\n`;
    csv += `Expected Closing Cash,${expectedClosingCash.toFixed(2)}\n`;

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `DayBook_${fromDate}_${toDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Day Book exported to CSV");
  };

  const handleSaveOpeningBalance = async () => {
    try {
      setIsSavingOB(true);
      await apiClient.post('/reports/day-book/opening-balance', {
        date: fromDate,
        amount: parseFloat(newOpeningBalance) || 0
      });
      toast.success("Opening balance updated successfully");
      setIsOpeningModalOpen(false);
      mutate();
    } catch (err) {
      toast.error(err.message || "Failed to update opening balance");
    } finally {
      setIsSavingOB(false);
    }
  };

  return (
    <Page>
      <div className="container-xl">
        {/* Sub-Navigation for Day Book as specified in Section 1 */}
        <div className="d-print-none mb-3">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <h2 className="page-title text-primary fw-bold mb-0">DAY BOOK</h2>
              <div className="text-muted small">Financial Activity & Comprehensive Audit Traceability</div>
            </div>
            <div className="btn-group shadow-sm">
              <button 
                className={`btn btn-sm ${activeTab === 'daily' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => { setActiveTab('daily'); handlePeriodPreset('today'); }}
              >
                Daily Report
              </button>
              <button 
                className={`btn btn-sm ${activeTab === 'range' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => { setActiveTab('range'); setFilterType('range'); }}
              >
                Date Range Report
              </button>
              <button 
                className={`btn btn-sm ${activeTab === 'transactions' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setActiveTab('transactions')}
              >
                Transaction Reports
              </button>
            </div>
          </div>
        </div>

        {/* SECTION 3: DATE FILTER FOR DAY BOOK */}
        <div className="card shadow-sm mb-3 d-print-none border-0">
          <div className="card-body p-3">
            <div className="row g-2 align-items-center">
              {/* Presets */}
              <div className="col-auto">
                <span className="fw-bold me-2 small text-muted text-uppercase">Report Period:</span>
                <div className="btn-group btn-group-sm">
                  <button 
                    className={`btn ${filterType === 'today' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => handlePeriodPreset('today')}
                  >
                    Today
                  </button>
                  <button 
                    className={`btn ${filterType === 'yesterday' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => handlePeriodPreset('yesterday')}
                  >
                    Yesterday
                  </button>
                  <button 
                    className={`btn ${filterType === 'custom' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => handlePeriodPreset('custom')}
                  >
                    Custom Date
                  </button>
                  <button 
                    className={`btn ${filterType === 'range' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setFilterType('range')}
                  >
                    Date Range
                  </button>
                </div>
              </div>

              {/* Date pickers */}
              <div className="col-auto d-flex align-items-center gap-2">
                <span className="small text-muted">From:</span>
                <input 
                  type="date" 
                  className="form-control form-control-sm" 
                  style={{ width: '140px' }}
                  value={fromDate}
                  onChange={(e) => {
                    setFromDate(e.target.value);
                    if (filterType !== 'range') setToDate(e.target.value);
                  }}
                />
                {filterType === 'range' && (
                  <>
                    <span className="small text-muted">To:</span>
                    <input 
                      type="date" 
                      className="form-control form-control-sm" 
                      style={{ width: '140px' }}
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                    />
                  </>
                )}
                <button 
                  className="btn btn-sm btn-dark px-3"
                  onClick={() => mutate()}
                >
                  Apply
                </button>
              </div>

              {/* Navigation Controls */}
              <div className="col-auto ms-md-auto d-flex gap-1 flex-wrap">
                <button 
                  className="btn btn-sm btn-outline-secondary" 
                  title="Previous Period"
                  onClick={() => handleShiftPeriod(-1)}
                >
                  <IconChevronLeft size={16} /> Prev
                </button>
                <button 
                  className="btn btn-sm btn-outline-secondary" 
                  title="Next Period"
                  onClick={() => handleShiftPeriod(1)}
                >
                  Next <IconChevronRight size={16} />
                </button>
                <button 
                  className="btn btn-sm btn-outline-secondary"
                  title="Refresh"
                  onClick={() => mutate()}
                >
                  <IconRefresh size={16} />
                </button>
                <button 
                  className="btn btn-sm btn-outline-primary"
                  title="Print"
                  onClick={() => window.print()}
                >
                  <IconPrinter size={16} className="me-1" /> Print
                </button>
                <button 
                  className="btn btn-sm btn-outline-success"
                  title="Export Excel / CSV"
                  onClick={handleExportCSV}
                >
                  <IconFileSpreadsheet size={16} className="me-1" /> Export Excel
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 4: DAY BOOK MAIN SCREEN / REPORT CARD */}
        {activeTab !== 'transactions' ? (
          <div className="card shadow-sm p-4 border-0 mb-4 bg-white">
            {/* Header with Store Info */}
            <div className="text-center mb-4 pb-3 border-bottom">
              <div className="badge bg-primary-lt text-primary px-3 py-1 mb-2 fw-bold text-uppercase letter-spacing-1">
                Official Financial Day Book
              </div>
              <h2 className="mb-1 fw-bold fs-1 text-dark">{storeName}</h2>
              <div className="text-muted fs-4">Location: <span className="text-dark fw-medium">{storeAddress}</span></div>
              <div className="text-muted fs-4">Phone: <span className="text-dark fw-medium">{storePhone}</span></div>
              {storePan && <div className="text-muted fs-4">PAN/VAT: <span className="text-dark fw-medium">{storePan}</span></div>}
              
              <div className="mt-3 fs-3 fw-bold text-primary">
                Period: {formatDisplayDate(fromDate)} {fromDate !== toDate ? ` — ${formatDisplayDate(toDate)}` : ''}
              </div>
            </div>

            {/* Loading Indicator */}
            {isLoading && (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status"></div>
                <div className="mt-2 text-muted">Calculating financial balances...</div>
              </div>
            )}

            {!isLoading && (
              <>
                <div className="row g-4">
                  {/* SALES SUMMARY (Left Column) */}
                  <div className="col-lg-6">
                    <div className="border rounded-3 p-3 h-100 bg-light-lt">
                      <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                        <h4 className="fw-bold text-uppercase text-secondary m-0">SALES SUMMARY</h4>
                        <span className="badge bg-blue-lt">Audited Figures</span>
                      </div>

                      <div className="table-responsive">
                        <table className="table table-vcenter table-hover table-bordered bg-white shadow-xs">
                          <thead className="table-light">
                            <tr>
                              <th>Item</th>
                              <th className="text-end">Amount</th>
                              <th className="text-center" style={{ width: '90px' }}>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td className="fw-medium">Gross Sales</td>
                              <td className="text-end fw-bold">
                                {currency} {grossSales.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                              <td className="text-center">
                                <button 
                                  className="btn btn-sm btn-icon btn-ghost-primary" 
                                  title="View Underlying Invoices"
                                  onClick={() => openDrillDown('Gross Sales Invoices', 'gross')}
                                >
                                  <IconEye size={17} />
                                </button>
                              </td>
                            </tr>
                            <tr>
                              <td className="fw-medium text-danger">Discount</td>
                              <td className="text-end text-danger fw-bold">
                                -{currency} {discount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                              <td className="text-center">
                                <button 
                                  className="btn btn-sm btn-icon btn-ghost-primary" 
                                  title="View Discounted Sales"
                                  onClick={() => openDrillDown('Discounted Invoices', 'discount')}
                                >
                                  <IconEye size={17} />
                                </button>
                                <button 
                                  className="btn btn-sm btn-icon btn-ghost-secondary ms-1" 
                                  title="Print Discount Report"
                                  onClick={() => window.print()}
                                >
                                  <IconPrinter size={17} />
                                </button>
                              </td>
                            </tr>
                            <tr>
                              <td className="fw-bold text-dark">Net Sales</td>
                              <td className="text-end fw-bold text-dark">
                                {currency} {netSales.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                              <td className="text-center">
                                <button 
                                  className="btn btn-sm btn-icon btn-ghost-primary" 
                                  title="View Net Sales Invoices"
                                  onClick={() => openDrillDown('Net Sales Invoices', 'net')}
                                >
                                  <IconEye size={17} />
                                </button>
                              </td>
                            </tr>
                            <tr>
                              <td className="fw-medium">VAT / Tax</td>
                              <td className="text-end fw-bold text-indigo">
                                {currency} {tax.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                              <td className="text-center">
                                <button 
                                  className="btn btn-sm btn-icon btn-ghost-primary" 
                                  title="View VAT Invoices"
                                  onClick={() => openDrillDown('Tax / VAT Invoices', 'tax')}
                                >
                                  <IconEye size={17} />
                                </button>
                                <button 
                                  className="btn btn-sm btn-icon btn-ghost-secondary ms-1" 
                                  title="Print VAT Invoices"
                                  onClick={() => window.print()}
                                >
                                  <IconPrinter size={17} />
                                </button>
                              </td>
                            </tr>
                            <tr>
                              <td className="fw-medium">Round Off</td>
                              <td className="text-end fw-medium text-muted">
                                {roundOff < 0 ? '-' : ''}{currency} {Math.abs(roundOff).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                              <td className="text-center">
                                <button 
                                  className="btn btn-sm btn-icon btn-ghost-primary" 
                                  title="View Round Off Logs"
                                  onClick={() => openDrillDown('Round Off Invoices', 'round_off')}
                                >
                                  <IconEye size={17} />
                                </button>
                                <button 
                                  className="btn btn-sm btn-icon btn-ghost-secondary ms-1" 
                                  title="Print"
                                  onClick={() => window.print()}
                                >
                                  <IconPrinter size={17} />
                                </button>
                              </td>
                            </tr>
                            <tr>
                              <td className="fw-medium text-purple">Complementary</td>
                              <td className="text-end fw-bold text-purple">
                                {currency} {complementary.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                              <td className="text-center">
                                <button 
                                  className="btn btn-sm btn-icon btn-ghost-primary" 
                                  title="View Complementary Items"
                                  onClick={() => openDrillDown('Complementary Orders', 'complementary')}
                                >
                                  <IconEye size={17} />
                                </button>
                                <button 
                                  className="btn btn-sm btn-icon btn-ghost-secondary ms-1" 
                                  title="Print"
                                  onClick={() => window.print()}
                                >
                                  <IconPrinter size={17} />
                                </button>
                              </td>
                            </tr>
                            <tr className="table-primary border-top border-dark">
                              <td className="fw-bold fs-3 text-primary">Total Billed</td>
                              <td className="text-end fw-bold fs-3 text-primary">
                                {currency} {totalBilled.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                              <td className="text-center">
                                <button 
                                  className="btn btn-sm btn-icon btn-primary" 
                                  title="View All Billed Invoices"
                                  onClick={() => openDrillDown('All Billed Invoices', 'billed')}
                                >
                                  <IconEye size={17} />
                                </button>
                                <button 
                                  className="btn btn-sm btn-icon btn-outline-primary ms-1" 
                                  title="Print"
                                  onClick={() => window.print()}
                                >
                                  <IconPrinter size={17} />
                                </button>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* COLLECTION SUMMARY (Right Column) */}
                  <div className="col-lg-6">
                    <div className="border rounded-3 p-3 h-100 bg-light-lt">
                      <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                        <h4 className="fw-bold text-uppercase text-secondary m-0">COLLECTION SUMMARY</h4>
                        <span className="badge bg-green-lt">Actual Payments</span>
                      </div>

                      <div className="table-responsive">
                        <table className="table table-vcenter table-hover table-bordered bg-white shadow-xs">
                          <thead className="table-light">
                            <tr>
                              <th>Payment Type</th>
                              <th className="text-end">Amount</th>
                              <th className="text-center" style={{ width: '90px' }}>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {salesPayments.map((p, idx) => {
                              const amt = Number(p.total_amount || 0);
                              return (
                                <tr key={idx}>
                                  <td className="fw-medium">
                                    <span className="badge bg-secondary-lt me-2 text-uppercase">{p.payment_method}</span>
                                  </td>
                                  <td className="text-end fw-bold">
                                    {currency} {amt.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                  <td className="text-center">
                                    <button 
                                      className="btn btn-sm btn-icon btn-ghost-primary" 
                                      title={`View ${p.payment_method} Collections`}
                                      onClick={() => openDrillDown(`${p.payment_method} Invoices`, `payment_${p.payment_method}`)}
                                    >
                                      <IconEye size={17} />
                                    </button>
                                    <button 
                                      className="btn btn-sm btn-icon btn-ghost-secondary ms-1" 
                                      title="Print"
                                      onClick={() => window.print()}
                                    >
                                      <IconPrinter size={17} />
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                            {salesPayments.length === 0 && (
                              <tr>
                                <td colSpan={3} className="text-center text-muted py-3">No payments recorded in this period</td>
                              </tr>
                            )}
                            <tr className="table-success border-top border-dark">
                              <td className="fw-bold fs-3 text-success">Total Collected</td>
                              <td className="text-end fw-bold fs-3 text-success">
                                {currency} {totalCollected.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                              <td className="text-center">
                                <button 
                                  className="btn btn-sm btn-icon btn-success" 
                                  title="View Collections"
                                  onClick={() => openDrillDown('All Collected Invoices', 'billed')}
                                >
                                  <IconEye size={17} />
                                </button>
                                <button 
                                  className="btn btn-sm btn-icon btn-outline-success ms-1" 
                                  title="Print"
                                  onClick={() => window.print()}
                                >
                                  <IconPrinter size={17} />
                                </button>
                              </td>
                            </tr>
                            <tr className="bg-yellow-lt">
                              <td className="fw-bold text-warning">Sales Due (Uncollected)</td>
                              <td className="text-end fw-bold text-warning">
                                {currency} {salesDue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                              <td className="text-center">
                                <button 
                                  className="btn btn-sm btn-icon btn-ghost-warning" 
                                  title="View Due Invoices"
                                  onClick={() => openDrillDown('Due Customer Receivables', 'due')}
                                >
                                  <IconEye size={17} />
                                </button>
                                <button 
                                  className="btn btn-sm btn-icon btn-ghost-secondary ms-1" 
                                  title="Print"
                                  onClick={() => window.print()}
                                >
                                  <IconPrinter size={17} />
                                </button>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      {/* Financial Balance Check Rule */}
                      <div className="alert alert-info py-2 px-3 mt-3 mb-0 d-flex justify-content-between align-items-center">
                        <div className="small">
                          <strong>Traceability Check:</strong> Total Billed ({currency} {totalBilled.toFixed(2)}) = Total Collected ({currency} {totalCollected.toFixed(2)}) + Sales Due ({currency} {salesDue.toFixed(2)})
                        </div>
                        <span className="badge bg-blue text-white">
                          Diff: {currency} {Math.abs(totalBilled - (totalCollected + salesDue)).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* SECTION 6: CASH FLOW */}
                <div className="mt-4 pt-2">
                  <div className="border rounded-3 p-4 bg-light-lt">
                    <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                      <div>
                        <h4 className="fw-bold text-uppercase text-secondary m-0">CASH FLOW (Physical Cash Movement Only)</h4>
                        <div className="text-muted small">Digital payments (QR, Card, Online) are excluded from drawer cash</div>
                      </div>
                      <button 
                        className="btn btn-sm btn-outline-primary d-print-none"
                        onClick={() => {
                          setNewOpeningBalance(String(openingBalance));
                          setIsOpeningModalOpen(true);
                        }}
                      >
                        Set Opening Balance
                      </button>
                    </div>

                    <div className="row g-4">
                      {/* Cash Opening & In */}
                      <div className="col-md-6">
                        <div className="card shadow-xs border-0 h-100">
                          <div className="card-header bg-white py-2">
                            <span className="fw-bold text-success d-flex align-items-center">
                              <IconArrowUpRight size={18} className="me-1" /> CASH OPENING & INFLOW
                            </span>
                          </div>
                          <div className="card-body p-0">
                            <table className="table table-vcenter m-0">
                              <tbody>
                                <tr className="table-light">
                                  <td className="fw-bold">Cash Opening Balance</td>
                                  <td className="text-end fw-bold text-dark">
                                    {currency} {openingBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                </tr>
                                <tr>
                                  <td>• Cash Sales (Billed Today)</td>
                                  <td className="text-end fw-medium">
                                    {currency} {cashSales.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                </tr>
                                <tr>
                                  <td>• Previous Due Collection</td>
                                  <td className="text-end fw-medium">
                                    {currency} {previousDueCollected.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                </tr>
                                <tr>
                                  <td>• Other Cash Receipts / Transfers In</td>
                                  <td className="text-end fw-medium">
                                    {currency} {otherCashReceipts.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                </tr>
                                <tr className="bg-success-lt fw-bold">
                                  <td>Total Cash Inflow</td>
                                  <td className="text-end text-success">
                                    +{currency} {totalCashIn.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>

                      {/* Cash Out & Closing */}
                      <div className="col-md-6">
                        <div className="card shadow-xs border-0 h-100">
                          <div className="card-header bg-white py-2">
                            <span className="fw-bold text-danger d-flex align-items-center">
                              <IconArrowDownRight size={18} className="me-1" /> CASH OUTFLOW & CLOSING
                            </span>
                          </div>
                          <div className="card-body p-0">
                            <table className="table table-vcenter m-0">
                              <tbody>
                                <tr>
                                  <td>• Cash Purchases</td>
                                  <td className="text-end fw-medium text-danger">
                                    -{currency} {cashPurchases.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                </tr>
                                <tr>
                                  <td>• Cash Expenses</td>
                                  <td className="text-end fw-medium text-danger">
                                    -{currency} {cashExpenses.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                </tr>
                                <tr>
                                  <td>• Cash Refunds</td>
                                  <td className="text-end fw-medium text-danger">
                                    -{currency} {cashRefunds.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                </tr>
                                <tr>
                                  <td>• Cash Withdrawals / Transfers Out</td>
                                  <td className="text-end fw-medium text-danger">
                                    -{currency} {cashWithdrawals.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                </tr>
                                <tr className="bg-danger-lt fw-bold">
                                  <td>Total Cash Outflow</td>
                                  <td className="text-end text-danger">
                                    -{currency} {totalCashOut.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Final Expected Closing Cash Banner */}
                    <div className="card mt-3 bg-dark text-white border-0 shadow-sm">
                      <div className="card-body py-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div>
                          <div className="text-uppercase small text-white-50 letter-spacing-1">
                            Formula: Opening Cash ({currency} {openingBalance.toFixed(2)}) + Cash In ({currency} {totalCashIn.toFixed(2)}) - Cash Out ({currency} {totalCashOut.toFixed(2)})
                          </div>
                          <div className="fs-2 fw-bold text-white">Expected Closing Cash</div>
                        </div>
                        <div className="fs-1 fw-bold text-success text-end">
                          {currency} {expectedClosingCash.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        ) : (
          /* SECTION: TRANSACTION REPORTS (FULL VIEW) */
          <div className="card shadow-sm border-0 mb-4 bg-white p-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h3 className="fw-bold m-0">All Financial Transactions ({data?.invoicesList?.length || 0})</h3>
              <button className="btn btn-sm btn-outline-primary" onClick={() => window.print()}>
                <IconPrinter size={16} className="me-1" /> Print All
              </button>
            </div>
            <div className="table-responsive">
              <table className="table table-vcenter table-hover table-striped">
                <thead>
                  <tr>
                    <th>Invoice #</th>
                    <th>Date / Time</th>
                    <th>Customer</th>
                    <th>Payment</th>
                    <th className="text-end">Subtotal</th>
                    <th className="text-end">Tax</th>
                    <th className="text-end">Discount</th>
                    <th className="text-end">Total</th>
                    <th className="text-end">Due</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.invoicesList || []).map((inv, idx) => (
                    <tr key={idx}>
                      <td className="fw-bold">{inv.invoice_no || `INV-${inv.id}`}</td>
                      <td className="small text-muted">{inv.created_at ? new Date(inv.created_at).toLocaleString() : '-'}</td>
                      <td>{inv.customer_name || 'Walk-in Customer'}</td>
                      <td><span className="badge bg-secondary-lt">{inv.payment_method_name || 'Cash'}</span></td>
                      <td className="text-end">{currency} {Number(inv.sub_total || 0).toFixed(2)}</td>
                      <td className="text-end">{currency} {Number(inv.tax_total || 0).toFixed(2)}</td>
                      <td className="text-end text-danger">{Number(inv.discount || 0) > 0 ? `-${Number(inv.discount).toFixed(2)}` : '0.00'}</td>
                      <td className="text-end fw-bold">{currency} {Number(inv.total || 0).toFixed(2)}</td>
                      <td className="text-end text-warning">{Number(inv.due_amount || 0) > 0 ? `${currency} ${Number(inv.due_amount).toFixed(2)}` : '-'}</td>
                      <td>
                        <span className={`badge ${inv.payment_status === 'paid' ? 'bg-success-lt' : 'bg-warning-lt'}`}>
                          {inv.payment_status || 'completed'}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {(!data?.invoicesList || data.invoicesList.length === 0) && (
                    <tr>
                      <td colSpan={10} className="text-center text-muted py-4">No transactions found for this period.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* DRILL-DOWN AUDIT MODAL (when clicking 👁) */}
        {drillModal.isOpen && (
          <div className="modal modal-blur fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content shadow-lg">
                <div className="modal-header bg-light">
                  <div>
                    <h5 className="modal-title fw-bold text-primary">{drillModal.title}</h5>
                    <div className="small text-muted">Complete financial traceability and underlying transaction records</div>
                  </div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setDrillModal({ isOpen: false, title: '', type: '', items: [] })}
                  ></button>
                </div>
                <div className="modal-body p-3">
                  {/* Search Bar */}
                  <div className="input-icon mb-3">
                    <span className="input-icon-addon">
                      <IconSearch size={18} />
                    </span>
                    <input 
                      type="text" 
                      className="form-control" 
                      placeholder="Search invoice number, customer, item or payment method..." 
                      value={drillSearch}
                      onChange={(e) => setDrillSearch(e.target.value)}
                    />
                  </div>

                  <div className="table-responsive" style={{ maxHeight: '450px' }}>
                    <table className="table table-vcenter table-hover table-striped">
                      <thead className="table-light sticky-top">
                        <tr>
                          <th>#</th>
                          <th>Invoice / Ref</th>
                          <th>Time</th>
                          <th>Customer / Item</th>
                          <th>Payment</th>
                          <th className="text-end">Amount ({currency})</th>
                          <th className="text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredDrillItems.map((item, idx) => (
                          <tr key={idx}>
                            <td>{idx + 1}</td>
                            <td className="fw-bold text-dark">{item.invoice_no || item.token_no || `ID-${item.id}`}</td>
                            <td className="small text-muted">{item.created_at || item.date ? new Date(item.created_at || item.date).toLocaleTimeString() : '-'}</td>
                            <td>{item.customer_name || item.item_name || 'Walk-in'}</td>
                            <td><span className="badge bg-secondary-lt">{item.payment_method_name || 'Cash'}</span></td>
                            <td className="text-end fw-bold">
                              {currency} {Number(item.total || (item.price * item.quantity) || item.amount || 0).toFixed(2)}
                            </td>
                            <td className="text-center">
                              <button 
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => window.print()}
                              >
                                <IconPrinter size={15} />
                              </button>
                            </td>
                          </tr>
                        ))}
                        {filteredDrillItems.length === 0 && (
                          <tr>
                            <td colSpan={7} className="text-center text-muted py-4">No matching records found.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="modal-footer bg-light d-flex justify-content-between">
                  <div className="fw-bold text-muted small">
                    Total Records: {filteredDrillItems.length}
                  </div>
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setDrillModal({ isOpen: false, title: '', type: '', items: [] })}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* OPENING BALANCE MODAL */}
        {isOpeningModalOpen && (
          <div className="modal modal-blur fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-sm modal-dialog-centered">
              <div className="modal-content shadow">
                <div className="modal-header">
                  <h5 className="modal-title fw-bold">Set Opening Balance</h5>
                  <button type="button" className="btn-close" onClick={() => setIsOpeningModalOpen(false)}></button>
                </div>
                <div className="modal-body">
                  <label className="form-label">Opening Cash Amount ({currency})</label>
                  <input 
                    type="number" 
                    step="any"
                    className="form-control" 
                    value={newOpeningBalance}
                    onChange={(e) => setNewOpeningBalance(e.target.value)}
                    placeholder="e.g. 10000"
                  />
                  <div className="small text-muted mt-2">
                    This sets the drawer opening float for {fromDate}.
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-link link-secondary" onClick={() => setIsOpeningModalOpen(false)}>
                    Cancel
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-primary ms-auto"
                    disabled={isSavingOB}
                    onClick={handleSaveOpeningBalance}
                  >
                    {isSavingOB ? 'Saving...' : 'Save Balance'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </Page>
  );
}
